// Strip field: fitter dropdown or selected pill with Change link.
// "Any Fitter" is the default. The list comes from /resources filtered to the
// selected location.

import { useEffect, useRef, useState } from 'react';
import styles from '../styles/widget.module.css';
import { listResources } from '../api/client';
import type { Resource } from '../types';
import type { BookingAction, BookingState } from '../state/machine';
import { useClickOutside } from '../hooks/useClickOutside';
import { useOpenClose } from '../hooks/useOpenClose';
import ChevronDown from '../assets/icons/ChevronDown';
import SimplePopup from './SimplePopup';
import { emitAnalytics } from '../analytics/dispatch';

interface FitterFieldProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  disabled?: boolean;
  hideSelectedPill?: boolean;
}

export function FitterField({ state, dispatch, disabled = false, hideSelectedPill = false }: FitterFieldProps) {
  const [fitters, setFitters] = useState<Resource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState<'down' | 'up'>('down');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const { isOpen: isOpenFitterInfo, close: closeFitterInfo, open: openFitterInfo } = useOpenClose();

  const wrapperRef = useClickOutside<HTMLDivElement>(() => {
    setOpen(false);
  }, { enabled: open });

  // The Fitter field sits at the bottom-right of the booking panel
  // grid, so there's always significantly more room above the trigger
  // than below it (especially inside the constrained modal). Default
  // to opening UP, and only fall back to DOWN when the space above is
  // genuinely too small — e.g. on a very short mobile viewport. The
  // earlier height-estimate heuristic flipped DOWN on tall desktop
  // viewports because viewport height alone gave the dropdown "enough"
  // room below in absolute terms, even though the modal clipped it.
  useEffect(() => {
    if (!open) return;
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceAbove = rect.top;
    // 240px is the rough natural dropdown height (header + ~5 rows).
    // If there's at least that much room above the trigger, open up.
    setDirection(spaceAbove >= 240 ? 'up' : 'down');
  }, [open]);

  useEffect(() => {
    if (!state.selectedLocation) return;
    listResources()
      .then((all) =>
        setFitters(all.filter((r) => r.locationId === state.selectedLocation!.id)),
      )
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load fitters'));
  }, [state.selectedLocation]);

  const selectedFitter = state.selectedFitterId
    ? fitters?.find((f) => f.id === state.selectedFitterId)
    : null;

  if (hideSelectedPill && (selectedFitter || state?.selectedTime)) return null

  if (selectedFitter) {
    return (
      <div className={styles.stripPill}>
        <span className={styles.label}>Fitter</span>
        <div className={styles.stripPillValue}>
          <span>{selectedFitter.name}</span>
          <button
            type="button"
            className={styles.stripPillChange}
            onClick={() => dispatch({ type: 'SELECT_FITTER', payload: null })}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.field} ${disabled ? styles.panelSectionDisabled : ''}`}
      style={{ position: 'relative' }}
      ref={wrapperRef}
    >
      <label className={styles.label}>
        Fitter{' '}
        <button type="button" onClick={openFitterInfo} className={styles.learnMoreButton}>
          Learn More
        </button>
      </label>

      {disabled && (
        <span className={styles.disabledFieldMessage}>Select a fitting type to enable this field</span>
      )}

      <SimplePopup
        children={
          <div>
            <h3>WHAT MAKES A MASTER FITTER</h3>
            <p>
              Our master fitters graduate from Club Champion University with over 250 hours of classroom
              and in bay fitting experience.To stay ahead of the curve, they undergo rigorous, ongoing
              education directly from golf&apos;s top manufacturers every single year. This continuous
              training builds unmatched, cross-brand product knowledge, ensuring they know exactly how
              every head and shaft combination performs for your unique swing. If you have an existing
              fitter relationship, choose that fitter here. If not, a master fitter will be assigned to
              you.
            </p>
          </div>
        }
        isOpen={isOpenFitterInfo}
        onClosePopup={closeFitterInfo}
        className={styles.fitterInfoPopup}
      />

      {error && <div className={styles.error}>{error}</div>}

      {/* The button + dropdown live inside a shared positioned wrapper so
          the dropdown can attach directly to the button's top/bottom edge
          without a gap. When direction='up' the dropdown sits flush above
          the button (covering the Fitter label by design — the field is
          in its active state). When direction='down' it sits flush below. */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          ref={triggerRef}
          className={styles.input}
          style={{ textAlign: 'left', cursor: 'pointer', background: '#fff', width: '100%' }}
          onClick={() => {
            if (disabled) return;
            setOpen(!open);
          }}
        >
          <span>Any Fitter</span>
          <ChevronDown />
        </button>

        {open && (
        <div
          className={styles.locationDropdown}
          style={
            direction === 'up'
              ? { position: 'absolute', bottom: '100%', left: 0, right: 0, zIndex: 20 }
              : { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20 }
          }
        >
          {!fitters ? (
            <div className={styles.locationDropdownHeader}>
              <span className={styles.spinner} /> Loading fitters…
            </div>
          ) : (
            <>
              <div className={styles.locationDropdownHeader}>
                {fitters.length + 1} Fitters to Choose From
              </div>
              <div
                className={styles.locationRow}
                onClick={() => {
                  if (disabled) return;
                  dispatch({ type: 'SELECT_FITTER', payload: null });
                  emitAnalytics({ event: 'book_flow_fitter_selected' });
                  setOpen(false);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (disabled) return;
                    dispatch({ type: 'SELECT_FITTER', payload: null });
                    emitAnalytics({ event: 'book_flow_fitter_selected' });
                    setOpen(false);
                  }
                }}
              >
                <div className={styles.locationName}>ANY FITTER</div>
              </div>
              {fitters.map((f) => (
                <div
                  key={f.id}
                  className={styles.locationRow}
                  onClick={() => {
                    if (disabled) return;
                    dispatch({ type: 'SELECT_FITTER', payload: f.id });
                    emitAnalytics({ event: 'book_flow_fitter_selected' });
                    setOpen(false);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (disabled) return;
                      dispatch({ type: 'SELECT_FITTER', payload: f.id });
                      emitAnalytics({ event: 'book_flow_fitter_selected' });
                      setOpen(false);
                    }
                  }}
                >
                  <div className={styles.locationName}>{f.name.toUpperCase()}</div>
                </div>
              ))}
            </>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
